export interface AnimalBagSelector<T> {
  next(): T;
  reset(): void;
}

function randomIndex(length: number, random: () => number): number {
  const value = random();
  if (!Number.isFinite(value)) throw new Error('Animal bag random source must return a finite number');
  return Math.min(length - 1, Math.max(0, Math.floor(value * length)));
}

export function createAnimalBagSelector<T>(
  candidates: readonly T[],
  random: () => number = Math.random,
): AnimalBagSelector<T> {
  if (candidates.length !== 5) throw new Error('Animal bag requires exactly 5 candidates');
  const source = [...candidates];
  let bag: T[] = [];
  let bagIndex = 0;
  let outputPosition = 6;
  let extraPosition = 2;

  const startBag = (): void => {
    bag = [...source];
    for (let index = bag.length - 1; index > 0; index -= 1) {
      const swapIndex = randomIndex(index + 1, random);
      [bag[index], bag[swapIndex]] = [bag[swapIndex]!, bag[index]!];
    }
    bagIndex = 0;
    outputPosition = 0;
    extraPosition = 2 + randomIndex(3, random);
  };

  return {
    next(): T {
      if (outputPosition === 6) startBag();
      outputPosition += 1;
      if (outputPosition === extraPosition) return source[randomIndex(source.length, random)]!;
      const selected = bag[bagIndex];
      if (selected === undefined) throw new Error('Animal bag was exhausted before its cycle completed');
      bagIndex += 1;
      return selected;
    },
    reset(): void {
      bag = [];
      bagIndex = 0;
      outputPosition = 6;
    },
  };
}
